import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { barterApi, messageApi, barterRequestApi } from '@/services/api'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth.store'
import { useToast } from '@/hooks/useToast'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import Modal from '@/components/ui/Modal'
import Alert from '@/components/ui/Alert'
import ReviewSection from '@/components/reviews/ReviewSection'
import ImageGallery from '@/components/barters/ImageGallery'
import { formatCurrency, formatDate, formatRelativeTime } from '@/utils/format'
import { ArrowLeft, Edit, Users, Send, MessageSquare } from 'lucide-react'
import type { Barter, Message } from '@/types'

export default function BarterView() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const toast = useToast()
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [selectedUserBarterId, setSelectedUserBarterId] = useState<number | null>(null)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [selectedRecipient, setSelectedRecipient] = useState<number | 'all' | null>(null)

  const { data: barterData, isLoading } = useQuery({
    queryKey: ['barter', id],
    queryFn: () => barterApi.get(Number(id)),
    enabled: !!id,
  })

  // Get barter images
  const { data: imagesData } = useQuery({
    queryKey: ['barter-images', id],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/barter-images?barter_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      return response.json()
    },
    enabled: !!id,
  })

  // Get user's own barters for group creation
  const { data: userBartersData } = useQuery({
    queryKey: ['user-barters'],
    queryFn: () => barterApi.list({}),
    enabled: showGroupModal,
  })

  // Get messages between current user and barter owner (only for non-owners)
  const { data: messagesData } = useQuery({
    queryKey: ['messages', id, barterData?.data?.barter?.user_id],
    queryFn: () => {
      const ownerId = barterData?.data?.barter?.user_id
      // Pass context_barter_id to filter messages by this specific barter
      return messageApi.getDirect(Number(ownerId), undefined, Number(id))
    },
    enabled: !!barterData?.data?.barter?.user_id &&
             !!barterData?.data?.barter?.allow_messages &&
             barterData?.data?.barter?.user_id !== user?.id, // Only for non-owners
    refetchInterval: () => {
      // Only poll when window is focused to reduce unnecessary refreshes
      return document.hasFocus() ? 5000 : false
    },
  })

  // Get all messages for barter owner (filtered by this barter)
  const { data: ownerMessagesData } = useQuery({
    queryKey: ['owner-messages', id],
    queryFn: () => messageApi.getDirect(undefined, Number(id)),
    enabled: !!id &&
             !!barterData?.data?.barter?.allow_messages &&
             barterData?.data?.barter?.user_id === user?.id, // Only for owners
    refetchInterval: () => {
      // Only poll when window is focused to reduce unnecessary refreshes
      return document.hasFocus() ? 5000 : false
    },
  })

  const sendMessageMutation = useMutation({
    mutationFn: ({ receiverId, messageText }: { receiverId: number; messageText: string }) =>
      // Always pass barter_id when sending messages from barter detail page
      messageApi.sendDirect(receiverId, messageText, Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', id] })
      queryClient.invalidateQueries({ queryKey: ['owner-messages', id] })
      setMessage('')
      toast.success('Message sent!')
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to send message')
    },
  })

  const createRequestMutation = useMutation({
    mutationFn: (data: { requester_barter_id: number; target_barter_id: number }) =>
      barterRequestApi.create(data),
    onSuccess: () => {
      toast.success('Barter request sent successfully!')
      setShowGroupModal(false)
      setSelectedUserBarterId(null)
      setTimeout(() => {
        navigate('/dashboard/requests')
      }, 1500)
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to create barter request')
    },
  })

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    if (isOwner) {
      // Owner sending message
      if (selectedRecipient === 'all') {
        // Send to all unique users who have messaged
        const uniqueUsers = Array.from(new Set(
          messages
            .filter((msg: Message) => msg.sender_id !== user?.id)
            .map((msg: Message) => msg.sender_id)
        )) as number[]

        if (uniqueUsers.length === 0) {
          toast.error('No users to send message to')
          return
        }

        // Send message to each user
        for (const userId of uniqueUsers) {
          await sendMessageMutation.mutateAsync({ receiverId: userId, messageText: message })
        }
        toast.success(`Message sent to ${uniqueUsers.length} user(s)!`)
        setMessage('')
      } else if (selectedRecipient) {
        // Send to specific user
        sendMessageMutation.mutate({ receiverId: selectedRecipient as number, messageText: message })
      } else {
        toast.error('Please select a recipient')
      }
    } else {
      // Non-owner sending to barter owner
      if (!barter?.user_id) return
      sendMessageMutation.mutate({ receiverId: barter.user_id, messageText: message })
    }
  }

  const handleStartBarterGroup = () => {
    setShowGroupModal(true)
    setSelectedUserBarterId(null)
    setError('')
  }

  const handleCreateGroup = () => {
    if (!barter || !selectedUserBarterId) return
    createRequestMutation.mutate({
      requester_barter_id: selectedUserBarterId,
      target_barter_id: barter.id
    })
  }

  if (isLoading) return <Loading />

  const barter = barterData?.data?.barter
  const userBarters = userBartersData?.data?.barters?.filter((b: Barter) => b.user_id === user?.id && b.status === 'open') || []

  // Create barter owner object for review prepopulation
  const barterOwner = barter ? {
    id: barter.user_id,
    first_name: barter.first_name,
    last_name: barter.last_name,
    email: barter.email,
    profession: barter.profession
  } : null
  const isOwner = barter?.user_id === user?.id
  const messages = isOwner
    ? ((ownerMessagesData?.data as any)?.messages || [])
    : ((messagesData?.data as any)?.messages || [])

  if (!barter) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Barter not found</p>
        <Button onClick={() => navigate('/dashboard/barters')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Barters
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="secondary" onClick={() => navigate('/dashboard/barters')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Barters
        </Button>
        {isOwner && (
          <Button onClick={() => navigate(`/dashboard/barters/${id}/edit`)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Barter
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Barter Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="space-y-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{barter.title}</h1>
                  <div className="flex items-center space-x-3 text-sm text-gray-500">
                    <span>Posted {formatDate(barter.created_at)}</span>
                    <span>•</span>
                    <span>
                      by{' '}
                      {isOwner ? (
                        <>
                          {barter.first_name} {barter.last_name}
                          <span className="ml-2 text-primary-600 font-medium">(You)</span>
                        </>
                      ) : (
                        <button
                          onClick={() => navigate(`/dashboard/users/${barter.user_id}`)}
                          className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
                        >
                          {barter.first_name} {barter.last_name}
                        </button>
                      )}
                    </span>
                  </div>
                </div>
                <Badge status={barter.status}>{barter.status.replace('_', ' ')}</Badge>
              </div>

              <div className="border-t pt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{barter.description}</p>
              </div>

              {barter.interested_in && (
                <div className="border-t pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Interested In</h2>
                  <p className="text-gray-700 whitespace-pre-wrap">{barter.interested_in}</p>
                </div>
              )}

              {/* Barter Images Gallery */}
              {imagesData?.data?.images && imagesData.data.images.length > 0 && (
                <div className="border-t pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Images</h2>
                  <ImageGallery images={imagesData.data.images} />
                </div>
              )}

              <div className="border-t pt-6 grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Type</h3>
                  <div className="flex items-center">
                    <Badge variant={barter.type === 'goods' ? 'primary' : 'success'}>
                      {barter.type}
                    </Badge>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Estimated Value</h3>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(barter.dollar_value)}</p>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-3">Location</h3>
                <p className="text-gray-900">
                  {barter.city || 'Any City'}
                  {barter.state && <>, {barter.state}</>}
                  , {barter.country || 'Any Country'}
                  {barter.continent && (
                    <>
                      <br />
                      <span className="text-gray-500">{barter.continent}</span>
                    </>
                  )}
                </p>
              </div>

              {barter.categories && barter.categories.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Categories</h3>
                  <div className="flex flex-wrap gap-2">
                    {barter.categories.map((cat, idx) => (
                      <Badge key={idx} variant="gray">{cat}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Private Messages Section */}
          {barter.allow_messages && (
            <Card title={isOwner ? "Messages from Interested Users" : "Contact Owner"}>
              {!isOwner && !barter.allow_messages ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">The owner has disabled private messaging for this barter.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="h-80 overflow-y-auto border rounded-lg p-4 bg-gray-50 space-y-3">
                    {messages.length === 0 ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-500 text-sm">
                            {isOwner ? 'No messages yet from interested users.' : 'No messages yet. Start a conversation!'}
                          </p>
                        </div>
                      </div>
                    ) : isOwner ? (
                      // Group messages by conversation for owner
                      (() => {
                        const conversations: Record<number, Message[]> = {}
                        messages.forEach((msg: Message) => {
                          const otherUserId = msg.sender_id === user?.id ? msg.receiver_id! : msg.sender_id!
                          if (!conversations[otherUserId]) {
                            conversations[otherUserId] = []
                          }
                          conversations[otherUserId].push(msg)
                        })

                        return Object.entries(conversations).map(([userIdStr, msgs]) => {
                          const userId = Number(userIdStr)
                          const otherUser = msgs.find((m: Message) => m.sender_id !== user?.id)
                          return (
                            <div key={userId} className="border rounded-lg p-3 bg-white mb-3">
                              <div className="flex items-center justify-between mb-2 pb-2 border-b">
                                <button
                                  onClick={() => navigate(`/dashboard/users/${userId}`)}
                                  className="text-sm font-semibold text-primary-600 hover:underline"
                                >
                                  {otherUser?.first_name} {otherUser?.last_name}
                                </button>
                                <span className="text-xs text-gray-500">{msgs.length} message(s)</span>
                              </div>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {msgs.map((msg: Message) => (
                                  <div
                                    key={msg.id}
                                    className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                                  >
                                    <div
                                      className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                                        msg.sender_id === user?.id
                                          ? 'bg-primary-600 text-white'
                                          : 'bg-gray-100 text-gray-900'
                                      }`}
                                    >
                                      <p className="text-xs opacity-75 mb-1">
                                        {msg.sender_id === user?.id ? 'You' : `${msg.first_name}`} • {formatRelativeTime(msg.created_at)}
                                      </p>
                                      <p>{msg.message}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })
                      })()
                    ) : (
                      // Regular message display for non-owners
                      messages.map((msg: Message) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                              msg.sender_id === user?.id
                                ? 'bg-primary-600 text-white'
                                : 'bg-white text-gray-900 border'
                            }`}
                          >
                            <p className="text-xs opacity-75 mb-1">
                              {msg.sender_id === user?.id ? (
                                'You'
                              ) : (
                                <button
                                  onClick={() => navigate(`/dashboard/users/${msg.sender_id}`)}
                                  className="hover:underline font-medium"
                                >
                                  {msg.first_name} {msg.last_name}
                                </button>
                              )}{' '}
                              • {formatRelativeTime(msg.created_at)}
                            </p>
                            <p className="text-sm">{msg.message}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <form onSubmit={handleSendMessage} className="space-y-3">
                    {isOwner && messages.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-700 mb-1 block">
                          Send to:
                        </label>
                        <select
                          className="select w-full"
                          value={selectedRecipient || ''}
                          onChange={(e) => setSelectedRecipient(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                          required
                        >
                          <option value="">Select recipient...</option>
                          <option value="all">All Users (Broadcast)</option>
                          {Array.from(new Set(
                            messages
                              .filter((msg: Message) => msg.sender_id !== user?.id)
                              .map((msg: Message) => msg.sender_id)
                          )).map((userId: unknown) => {
                            const userIdNum = Number(userId)
                            const userMsg = messages.find((msg: Message) => msg.sender_id === userIdNum)
                            return (
                              <option key={userIdNum} value={userIdNum}>
                                {userMsg?.first_name} {userMsg?.last_name}
                              </option>
                            )
                          })}
                        </select>
                      </div>
                    )}
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        className="input flex-1"
                        placeholder={isOwner ? "Type your message..." : "Type your message..."}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={sendMessageMutation.isPending}
                      />
                      <Button type="submit" disabled={!message.trim()} isLoading={sendMessageMutation.isPending}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                </div>
              )}
            </Card>
          )}

          {/* Reviews Section */}
          <Card>
            <ReviewSection type="barter" id={Number(id)} barterOwner={barterOwner as any} />
          </Card>
        </div>

        {/* Sidebar Actions */}
        <div className="space-y-6">
          <Card title="Actions">
            <div className="space-y-3">
              {!isOwner && barter.status === 'open' && (
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={handleStartBarterGroup}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Start Barter Group
                </Button>
              )}

              {isOwner && (
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => navigate(`/dashboard/barters/${id}/edit`)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Barter
                </Button>
              )}
            </div>
          </Card>

          <Card title="Owner Information">
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Name</h3>
                <p className="mt-1 text-gray-900">
                  {barter.first_name} {barter.last_name}
                  {isOwner && <span className="ml-2 text-primary-600 text-sm">(You)</span>}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Select Your Barter Modal */}
      {showGroupModal && (
        <Modal
          title="Select Your Barter"
          onClose={() => setShowGroupModal(false)}
          size="lg"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900">Creating group with:</h3>
              <p className="text-sm text-blue-700 mt-1">
                {barter.title} by {barter.first_name} {barter.last_name}
              </p>
            </div>

            <div>
              <label className="label">Select which of your barters to add to this group *</label>
              <p className="text-sm text-gray-500 mb-3">
                Choose one of your open barters to trade with this barter
              </p>

              {userBarters.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-600 mb-3">You don't have any open barters</p>
                  <Button onClick={() => navigate('/dashboard/barters/create')}>
                    Create a Barter
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {userBarters.map((userBarter: Barter) => (
                    <label
                      key={userBarter.id}
                      className={`flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                        selectedUserBarterId === userBarter.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <input
                        type="radio"
                        name="userBarter"
                        value={userBarter.id}
                        checked={selectedUserBarterId === userBarter.id}
                        onChange={() => setSelectedUserBarterId(userBarter.id)}
                        className="mt-1 mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{userBarter.title}</div>
                        <div className="text-sm text-gray-500 mt-1">
                          {userBarter.description?.substring(0, 100)}...
                        </div>
                        <div className="flex items-center space-x-2 mt-2">
                          <Badge variant={userBarter.type === 'goods' ? 'primary' : 'success'}>
                            {userBarter.type}
                          </Badge>
                          <span className="text-sm font-medium">{formatCurrency(userBarter.dollar_value)}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <Alert type="error" message={error} onClose={() => setError('')} />
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="secondary" onClick={() => setShowGroupModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateGroup}
                disabled={!selectedUserBarterId}
                isLoading={createRequestMutation.isPending}
              >
                <Users className="w-4 h-4 mr-2" />
                Send Request
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
